import { queryOptions } from '@tanstack/react-query';
import { ApiError, isConnectivityError } from '../../api/client';
import { getOnboardingRecord, isRecordNotReady, onboardingRecordQueryKey } from '../../api/onboardingRecord';

/**
 * Whether a failed read is worth another try: a 5xx or a lost connection. A cancelled step-up or a
 * 4xx is never retried, so the prompt is not shown again a second later. The landing's reads and the
 * form's reads all use this. A 409 ONBOARDING_NOT_READY is a 4xx and so is not retried either: the
 * form polls through it (`refetchInterval`) instead.
 */
export const isTransientReadFailure = (err: unknown) => (err instanceof ApiError && err.status >= 500) || isConnectivityError(err);

/**
 * The client's onboarding record, declared ONCE (steward-app#673: a key with a `queryFn` is declared
 * in one place). The landing (`OnboardingClientGate`) and the form (`OnboardingForm`) both read it,
 * under one key, so they share one policy:
 *
 *   - `gcTime: 0` and `refetchOnMount: 'always'`: nothing is kept after the last reader unmounts, and
 *     a mount never trusts a copy another reader left. The form's body takes its answers from the
 *     record once, so an older copy would be an older base than the record now holds (a client who
 *     comes back by the browser's Back button within the default five minutes would see v1 answers).
 *   - transient failures retry (twice), so a blip is not read as "not sent" or as a failed form.
 *   - "still being prepared" is polled through.
 */
export const onboardingRecordQuery = (token: string, firmSlug: string | undefined, enabled: boolean) =>
  queryOptions({
    queryKey: onboardingRecordQueryKey(firmSlug ?? ''),
    queryFn: () => getOnboardingRecord(token, firmSlug!),
    enabled: enabled && !!firmSlug,
    retry: (count: number, err: unknown) => isTransientReadFailure(err) && count < 2,
    refetchInterval: (query) => (query.state.data === undefined && isRecordNotReady(query.state.error) ? 4000 : false),
    staleTime: Infinity,
    gcTime: 0,
    refetchOnMount: 'always',
  });
